"use client";

import type { ReactNode, CSSProperties } from "react";
import { useMemo } from "react";
import Image from "next/image";
import { useNextSanityImage } from "next-sanity-image";
import { stegaClean } from "next-sanity";
import { cn } from "@repo/ui/utils.ts";
import { client } from "@/lib/sanity/client";
import { buildSanityImageBuilder, parseCropAspect, type CropAspect } from "@/lib/sanity/image-loader";
import { getObjectPositionFromHotspot } from "@/lib/utils";
import type { ImageType, Maybe } from "@/lib/types";
import type { SanityImageAsset } from "@/lib/sanity/sanity.types";
import { RichTextCaption } from "@/components/common/rich-text/rich-text-caption.component";
export interface ImageWrapperProps {
  visual: ImageType | null;
  src?: string;
  alt?: string;
  unoptimized?: boolean;
  className?: string;
  sizes?: string;
  children?: ReactNode;
  imgClassName?: string;
  noBlur?: boolean;
  loading?: "eager" | "lazy" | undefined;
  priority?: boolean;
  fetchPriority?: "high" | "low" | "auto";
  visualOrientation?: string;
  resizeMode?: "contain" | "cover";
  cropAspect?: CropAspect;
}
export function ImageWrapper({
  visual,
  src,
  alt,
  unoptimized,
  sizes = "(max-width: 1800px) 100vw, 1800px",
  className = "",
  imgClassName,
  noBlur,
  priority,
  fetchPriority,
  visualOrientation,
  resizeMode,
  cropAspect,
  children: _,
  ...imagePropsWithoutChildren
}: ImageWrapperProps): JSX.Element | null {
  // children is destructured above to exclude it from imagePropsWithoutChildren
  // Next.js Image component does not accept children prop
  void _;
  const parsedCropAspect = parseCropAspect(cropAspect);
  const imageBuilder = useMemo(() => buildSanityImageBuilder({
    cropAspect: parsedCropAspect
  }), [parsedCropAspect]);
  const imageProps = useNextSanityImage(client, visual?.image || null, {
    imageBuilder
  });
  const resolvedSrc = src ?? imageProps?.src;
  if (!resolvedSrc || !src && !visual) return null;
  const resolvedFetchPriority = fetchPriority ?? (priority ? "high" : undefined);
  const cropAspectCss = parsedCropAspect ? `${parsedCropAspect}` : undefined;
  const aspectRatio = src ? undefined : cropAspectCss ?? (visualOrientation === "0" || !visualOrientation ? imageProps ? `${imageProps.width}/${imageProps.height}` : undefined : visualOrientation);
  const style: CSSProperties | undefined = aspectRatio ? {
    aspectRatio
  } : undefined;
  const objectPosition = getObjectPositionFromHotspot(visual?.image?.hotspot || {
    x: 0.5,
    y: 0.5
  });
  const effectiveResizeMode = resizeMode ?? stegaClean(visual?.resizeMode);
  const objectFit = effectiveResizeMode === "contain" ? "object-contain" : "object-cover";
  return <div className={cn("relative", className)} style={style}>
      <Image alt={alt ?? visual?.alt ?? (visual?.image?.asset as Maybe<SanityImageAsset>)?.altText ?? ""} blurDataURL={visual?.image?.asset?.metadata?.lqip || ""} className={cn(objectFit, imgClassName)} fill loader={src ? undefined : imageProps?.loader} placeholder={src || noBlur || !visual?.image?.asset?.metadata?.lqip ? "empty" : "blur"} priority={priority} fetchPriority={resolvedFetchPriority} quality={85} sizes={sizes} src={resolvedSrc} style={{
      objectPosition
    }} unoptimized={unoptimized} {...imagePropsWithoutChildren} />
      {visual?.caption ? <RichTextCaption richText={visual.caption} className="typography-body-md-regular" /> : null}
    </div>;
}